import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, parseApiError, type Me } from '../lib/api';
import { useEffectiveMe } from '../lib/viewAs';
import { cn } from '../lib/cn';
import { Card } from './Card';
import { MORPH } from '../lib/motion';
import { Link2 } from 'lucide-react';

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

// Moved out of Organizations.tsx (org-step-segmentation consolidation) —
// Discord guild bind/unbind. Unlike the other three founder-only panels in
// this slice, view access is founder OR admin; only founders can edit —
// kept as the original `me` prop (not collapsed to a single isFounder bool)
// since the internal gate does more than a single founder check.
export function GuildConnectionPanel({ me, orgId }: { me?: Me; orgId: string }) {
  const qc = useQueryClient();
  const [guildId, setGuildId] = useState('');
  const [error, setError] = useState('');
  // Display-only — set from bindOrgGuild's response when present, never
  // depended on for logic (contract note: backend may not send it yet).
  const [importQueuedNotice, setImportQueuedNotice] = useState(false);
  // View-as-aware copy of the `me` prop — BOTH gates below read THIS, not the
  // raw prop. Bind/rebind/unbind repoints which Discord community a real org's
  // bot buttons and clan views resolve against, so a founder previewing a
  // lower role must lose it: previewing 'admin' keeps view but loses edit,
  // previewing 'member' loses the panel entirely. Applied HERE rather than at
  // the call site so the gate is self-managing — a future caller that hands
  // over a raw `me` cannot reopen the hole. Idempotent if the caller already
  // passed an effective `me`, and an identity no-op whenever no view-as is
  // active — see lib/viewAs.ts's useEffectiveMe.
  const effectiveMe = useEffectiveMe(me);
  const canView = effectiveMe?.role === 'founder' || effectiveMe?.role === 'admin';
  const canEdit = effectiveMe?.role === 'founder';

  const current = useQuery({
    queryKey: ['orgGuild', orgId],
    queryFn: () => api.orgGuild(orgId),
    enabled: Boolean(orgId) && canView,
  });

  const mut = useMutation({
    mutationFn: (value: string | null) => api.bindOrgGuild(orgId, value),
    onSuccess: data => {
      setError('');
      setGuildId('');
      setImportQueuedNotice(data.memberImport?.state === 'QUEUED');
      qc.invalidateQueries({ queryKey: ['orgGuild', orgId] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (!canView) return null;
  const bound = current.data?.primaryGuildId ?? null;

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <Link2 className="h-4 w-4 text-ink-secondary" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Discord Server Connection</h2>
      </div>
      <p className="mb-3 text-xs text-ink-meta">
        The bound server decides which Discord community this org's bot buttons and clan views belong to.
        One server per org.
      </p>
      {bound ? (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.04] px-3 py-2">
          <p className="font-mono text-sm text-ink-body">{bound}</p>
          {canEdit && (
            <button
              className="text-xs text-ink-secondary hover:text-red-400"
              onClick={() => mut.mutate(null)}
              disabled={mut.isPending}
            >
              Unbind
            </button>
          )}
        </div>
      ) : (
        <p className="rounded-lg bg-white/[0.04] px-3 py-2 text-xs text-ink-secondary">
          No Discord server bound — bot interactions can't scope to this org yet.
        </p>
      )}
      {canEdit && (
        <>
          <div className="mt-3 flex gap-2">
            <input
              className="input flex-1"
              placeholder="Discord server (guild) ID"
              value={guildId}
              onChange={e => setGuildId(e.target.value.trim())}
            />
            <button
              className={cn('btn-primary inline-flex items-center gap-2', MORPH)}
              disabled={!/^\d{17,20}$/.test(guildId) || mut.isPending}
              onClick={() => mut.mutate(guildId)}
            >
              <Link2 className="h-4 w-4" />
              {mut.isPending ? 'Binding…' : bound ? 'Rebind' : 'Bind'}
            </button>
          </div>
          {/* Fix 2 (duplicate-org seam): installing the bot on a fresh server
              already creates and binds an org automatically — this field only
              matters for a portal-created org that was born without one. */}
          <p className="mt-2 text-[11px] text-ink-meta">
            Installing the bot on a fresh server creates and binds an org automatically — this field is only for
            linking a portal-created org to a server after the fact.
          </p>
          <p className="mt-1 text-[11px] text-ink-meta">
            Find the server ID in Discord: enable Developer Mode (User Settings → Advanced), then right-click the
            server icon → Copy Server ID.
          </p>
        </>
      )}
      {importQueuedNotice && (
        <p className="mt-2 text-[11px] italic text-ink-meta">
          Member import queued — check Org Members shortly.
        </p>
      )}
      <ErrorMsg msg={error} />
    </Card>
  );
}
